import { TFunction } from "i18next";
import { SideBarRoutes } from "../routes/types/SideBarRoutes.enum";
import { Form } from "../model/form/Form";
import { Status } from "../model/shared/enum/Status.enum";
import { Flex } from "../components/Flex";
import { FontSize, Text } from "../components/Text";
import { KanbanCard } from "../model/kanban/KanbanCard";
import React, { Fragment } from "react";
import { ActivityEntityKind } from "../model/activity/types/ActivityEntityKind.enum";
import { AtiraIcon } from "../components/AtiraIcon";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import { GetReportsResponseDto } from "../model/reports/dto/GetReportsResponseDto";
import { toCurrency } from "./pricingUtils";
import { standardDate } from "./Date";
import { Entry } from "../model/entry/Entry";
import { BaseTask } from "../model/task/Task";
import { ModuleDefinition } from "../model/module/types/module-definition/ModuleDefinition";
import { BaseModuleInstance } from "../model/module/types/module-instance/BaseModuleInstance";
import map from "lodash/map";
import styled from "styled-components";
import { Rounded } from "../theme/Rounded";
import { Spacing } from "../theme/Spacing";
import { TextWithTruncated } from "../components/TextWithTruncated";
import { NavigateFunction } from "react-router-dom";
import { ThemeType } from "../theme/Theme";
import { EntryKind } from "../model/entry/types/EntryKind.enum";
import { PieLabelRenderProps } from "recharts/types/polar/Pie";
import { Range } from "../model/shared/dto/GenericWithRange";
import { ReportKinds } from "../model/reports/types/ReportsKinds.enum";
import { TaskFilterReminder } from "../model/task/types/TaskFilterReminder.enum";
import { KanbanCardDateField } from "../model/kanban/types/KanbanCardDateField.enum";
import { KanbanColumnStageType } from "../model/kanban/types/KanbanColumnStageType.enum";
import { EntityIcons } from "./GlobalIcons";

/** A thin accent instead of a full-bleed fill, so a card reads as "this kind,
 * on the page's normal white surface" rather than as a solid block of colour
 * competing with every other card next to it. */
export const ItemCard = styled(Flex)<{ accent?: string }>`
  border-radius: ${Rounded.md};
  background: ${({ theme }) => theme.white};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border-inline-start: 3px solid ${({ accent, theme }) => accent || theme.main};
  flex-direction: column;
  gap: ${Spacing.s};
  padding: ${Spacing.s} ${Spacing.m};
  width: 100%;
  max-width: 35rem;
`;

export const ActionChip = styled(Text)`
  background: ${({ theme }) => theme.sub};
  border: 1px solid ${({ theme }) => theme.accent};
  border-radius: 999px;
  padding: 2px 10px;
  font-size: ${FontSize.s};
`;

const CircleIcon = <AtiraIcon width={5} icon={faCircle} color="main" />;

/** One resource in a member's activity list, with every action taken on it. */
export type ReportEntityItem =
  GetReportsResponseDto["metricsDataCentric"][number]["byEntity"][ActivityEntityKind][number];

/**
 * Activity metadata hangs off individual events, but the things read from it
 * here (which flavour of module, which entry an attachment belongs to) are
 * fixed for the resource, so the first event that carries one will do.
 */
const findMeta = <T,>(
  item: ReportEntityItem,
  pick: (
    meta: NonNullable<
      ReportEntityItem["actions"][number]["data"][number]["meta"]
    >,
  ) => T | undefined,
): T | undefined => {
  for (const action of item?.actions || []) {
    for (const event of action.data || []) {
      const found = event.meta ? pick(event.meta) : undefined;
      if (found !== undefined && found !== null) {
        return found;
      }
    }
  }

  return undefined;
};

const getModuleKind = (item: ReportEntityItem) =>
  findMeta(item, (meta) => meta.shared?.moduleKind);

/** The best name the activity itself recorded, for an entity that is now gone.
 *
 * A deleted record has no lookup result, so the reader would otherwise get a
 * bare id. The activity that deleted, created or renamed it still carries the
 * name it had at the time, which is a good deal more use than `#65f3...`.
 */
const getRecordedEntityName = (item: ReportEntityItem): string =>
  findMeta(
    item,
    (meta) =>
      meta.DELETE?.fields?.name ||
      meta.DELETE?.fields?.title ||
      meta.INSERT?.name ||
      meta.INSERT?.title ||
      meta.PATCH?.name?.to ||
      meta.PATCH?.title?.to,
  ) || "";

export const getEntityTitle = (
  item: ReportEntityItem,
  t: TFunction,
): React.ReactNode => {
  const entity = item?.entity;
  const kind = entity?.entityKind;
  const recordedName = getRecordedEntityName(item);
  const defaultTitle = recordedName
    ? `${recordedName} (${t("common.deleted")})`
    : `#${item.resourceId}`;
  // Activities outlive the things they describe, so a since-deleted entity has
  // no lookup result and only the activity's own timestamp to date it by.
  const createdAt = entity?.createdAt || item?.latestCreatedAt || "";

  if (kind === ActivityEntityKind.KANBAN_CARD) {
    const { note, title, amount, currency, closedByUser } = (entity ||
      {}) as Partial<KanbanCard>;

    return (
      <Flex
        gap="xs"
        alignItems="flex-start"
        flexDirection="column"
      >
        <TextWithTruncated fontWeight={"bold"} length={70}>
          {title || defaultTitle}
        </TextWithTruncated>
        <Flex alignItems="center" gap="xs" flexWrap="wrap">
          {kind ? <Text>{t(`common.${kind?.toLowerCase()}`)}</Text> : null}
          {amount ? (
            <Fragment>
              {CircleIcon}
              <Text>{toCurrency(Number(amount) * 100, currency || "USD")}</Text>
            </Fragment>
          ) : null}
          {CircleIcon}
          <Text color={closedByUser?._id ? "success" : "danger"}>
            {closedByUser?._id ? t("common.done") : t("common.undone")}
          </Text>
          {note ? (
            <Fragment>
              {CircleIcon}
              <TextWithTruncated length={70}>{note}</TextWithTruncated>
            </Fragment>
          ) : null}
        </Flex>
        <Text color="darkerSub" fontSize="s">
          {t("common.createdAt")}: {standardDate<string>(createdAt)}
        </Text>
      </Flex>
    );
  }

  if (kind === ActivityEntityKind.ENTRY) {
    const entry = entity as Partial<Entry>;
    const label = [
      entry?.fields?.name,
      entry?.fields?.email,
      entry?.fields?.phone,
    ].filter(Boolean);

    return (
      <Flex
        gap="xs"
        alignItems="flex-start"
        flexDirection="column"
      >
        <Flex alignItems="center" gap="xs" flexDirection="row" flexWrap="wrap">
          {label.length ? (
            label.map((l, i, thisArray) => (
              <Fragment key={`${item.resourceId}-${i}-${l}`}>
                <TextWithTruncated fontWeight={"bold"} length={70}>
                  {l}
                </TextWithTruncated>
                {i !== thisArray.length - 1 ? CircleIcon : null}
              </Fragment>
            ))
          ) : (
            <TextWithTruncated fontWeight={"bold"} length={70}>
              {defaultTitle}
            </TextWithTruncated>
          )}
        </Flex>
        <Flex alignItems="center" gap="xs" flexWrap="wrap">
          {entry?.kind ? (
            <Text>{t(`common.${entry.kind.toLowerCase()}`)}</Text>
          ) : null}

          {entry?.description ? (
            <TextWithTruncated length={70}>
              {entry.description}
            </TextWithTruncated>
          ) : null}
          {entry?.origin ? (
            <Fragment>
              {CircleIcon}
              <TextWithTruncated length={70}>{entry.origin}</TextWithTruncated>
            </Fragment>
          ) : null}
        </Flex>
        <Text color="darkerSub" fontSize="s">
          {t("common.createdAt")}: {standardDate<string>(createdAt)}
        </Text>
      </Flex>
    );
  }

  if (kind === ActivityEntityKind.TASK) {
    const {
      description,
      name,
      kind: taskKind,
      done,
    } = (entity || {}) as Partial<BaseTask>;

    return (
      <Flex
        gap="xs"
        alignItems="flex-start"
        flexDirection="column"
      >
        <TextWithTruncated fontWeight={"bold"} length={70}>
          {name || defaultTitle}
        </TextWithTruncated>
        <Flex alignItems="center" gap="xs" flexWrap="wrap">
          {taskKind ? (
            <Text>{t(`common.${taskKind.toLowerCase()}`)}</Text>
          ) : null}
          {CircleIcon}
          <Text color={done ? "success" : "danger"}>
            {done ? t("common.done") : t("common.undone")}
          </Text>
          {description ? (
            <Fragment>
              {CircleIcon}
              <TextWithTruncated length={70}>{description}</TextWithTruncated>
            </Fragment>
          ) : null}
        </Flex>
        <Text color="darkerSub" fontSize="s">
          {t("common.createdAt")}: {standardDate<string>(createdAt)}
        </Text>
      </Flex>
    );
  }

  if (kind === ActivityEntityKind.FORM) {
    const { title, status } = (entity || {}) as Partial<Form>;

    return (
      <Flex
        gap="xs"
        alignItems="flex-start"
        flexDirection="column"
      >
        <TextWithTruncated fontWeight={"bold"} length={70}>
          {title || defaultTitle}
        </TextWithTruncated>

        <Flex alignItems="center" gap="xs" flexWrap="wrap">
          <Text>{t("common.form")}</Text>
          {status ? (
            <Fragment>
              {CircleIcon}
              <Text color={status === Status.ACTIVE ? "success" : "danger"}>
                {status === Status.ACTIVE
                  ? t("common.active")
                  : t("common.inactive")}
              </Text>
            </Fragment>
          ) : null}
        </Flex>

        <Text color="darkerSub" fontSize="s">
          {t("common.createdAt")}: {standardDate<string>(createdAt)}
        </Text>
      </Flex>
    );
  }

  if (kind === ActivityEntityKind.MODULE) {
    const { name } = entity as Partial<ModuleDefinition | BaseModuleInstance>;
    const fields = map(
      (entity as Partial<BaseModuleInstance>)?.fields || {},
      (v) => String(v),
    );
    const label = [name, ...fields].filter(Boolean).slice(0, 3);

    return (
      <Flex
        gap="xs"
        alignItems="flex-start"
        flexDirection="column"
      >
        <Flex alignItems="center" gap="xs" flexDirection="row" flexWrap="wrap">
          {label.length ? (
            label.map((l, i, thisArray) => (
              <Fragment key={`${item.resourceId}-${i}-${l}`}>
                <TextWithTruncated fontWeight={"bold"} length={70}>
                  {l}
                </TextWithTruncated>
                {i !== thisArray.length - 1 ? CircleIcon : null}
              </Fragment>
            ))
          ) : (
            <TextWithTruncated fontWeight={"bold"} length={70}>
              {defaultTitle}
            </TextWithTruncated>
          )}
        </Flex>

        <Text color="darkerSub" fontSize="s">
          {t("common.createdAt")}: {standardDate<string>(createdAt)}
        </Text>
      </Flex>
    );
  }

  return <Text>{defaultTitle}</Text>;
};

export const onViewEntity = (
  resource: ReportEntityItem,
  context: {
    setEntryDetailsVisible: (visible: boolean) => void;
    setEntry: (entry: Entry) => void;
    setTaskReadVisible: (visible: boolean) => void;
    setTask: (task: BaseTask) => void;
    setKanbanCard: (card: KanbanCard) => void;
    setKanbanCardReadVisible: (visible: boolean) => void;
    setModuleInstance: (instance: BaseModuleInstance) => void;
    setModuleInstanceReadVisible: (visible: boolean) => void;
  },
  navigate: NavigateFunction,
) => {
  const {
    setEntryDetailsVisible,
    setEntry,
    setTaskReadVisible,
    setTask,
    setKanbanCard,
    setKanbanCardReadVisible,
    setModuleInstance,
    setModuleInstanceReadVisible,
  } = context;

  if (!resource) {
    return;
  }

  const kind = resource.entity?.entityKind;

  if (kind === ActivityEntityKind.ENTRY) {
    setEntry({
      _id: resource.entity?._id!,
      kind: (resource.entity as Partial<Entry>)?.kind,
    } as Entry);
    setEntryDetailsVisible(true);
  }

  if (kind === ActivityEntityKind.TASK) {
    setTask({
      _id: resource.entity?._id!,
      kind: (resource.entity as Partial<BaseTask>)?.kind,
    } as BaseTask);
    setTaskReadVisible(true);
  }

  if (kind === ActivityEntityKind.KANBAN_CARD) {
    setKanbanCard({ _id: resource.entity?._id! } as KanbanCard);
    setKanbanCardReadVisible(true);
  }

  // Forms have no read drawer, so the eye button goes to the forms page rather
  // than doing nothing at all.
  if (kind === ActivityEntityKind.FORM) {
    navigate(SideBarRoutes.FORMS);
    return;
  }

  if (kind === ActivityEntityKind.MODULE) {
    const moduleKind = getModuleKind(resource);

    // An attachment has no view of its own; the entry it hangs off does.
    if (moduleKind === "MODULE_INSTANCE_ATTACHMENT") {
      const entryId = findMeta(resource, (meta) => meta.INSERT?.entryId);

      if (entryId) {
        setEntry({ _id: entryId } as Entry);
        setEntryDetailsVisible(true);
      }
      return;
    }

    if (moduleKind === "MODULE_DEFINITION") {
      navigate(`/modules/${resource.resourceId}`);
      return;
    }

    setModuleInstance({
      _id: resource.entity?._id!,
      parentId: (resource.entity as Partial<BaseModuleInstance>)?.parentId!,
      entryId: (resource.entity as Partial<BaseModuleInstance>)?.entryId!,
    } as BaseModuleInstance);
    setModuleInstanceReadVisible(true);
  }
};

export const getKindLabel = (kind: string, t: TFunction) => {
  return t(`common.${kind.toLowerCase()}`);
};

/**
 * The theme key for a kind's category colour, not the resolved value - so a
 * caller that needs a theme key directly (`AtiraIcon`'s `color` prop, for
 * one) doesn't have to reverse-engineer one out of a CSS string.
 *
 * Tasks used to map to `danger` (red), which reads as an error state on a
 * card or chart line that is just describing an ordinary task.
 */
export const getKindColorKey = (kind: string): keyof ThemeType => {
  switch (kind) {
    case ActivityEntityKind.ENTRY:
      return "main";
    case ActivityEntityKind.TASK:
      return "warning";
    case ActivityEntityKind.KANBAN_CARD:
      return "black";
    case ActivityEntityKind.MODULE:
      return "positive";
    case ActivityEntityKind.FORM:
      return "darkerSub";
    default:
      return "main";
  }
};

export const getKindColor = (kind: string, theme: ThemeType) =>
  theme[getKindColorKey(kind)];

export const getKindIcon = (kind: string) => {
  switch (kind) {
    case ActivityEntityKind.ENTRY:
      return EntityIcons.Entries;
    case ActivityEntityKind.TASK:
      return EntityIcons.Tasks;
    case ActivityEntityKind.KANBAN_CARD:
      return EntityIcons.Deals;
    case ActivityEntityKind.MODULE:
      return EntityIcons.Modules;
    case ActivityEntityKind.FORM:
      return EntityIcons.Forms;
    default:
      return EntityIcons.Entries;
  }
};

const RADIAN = Math.PI / 180;

const ellipsis = (s: string, maxChars: number) => {
  const str = String(s ?? "");
  if (maxChars <= 0) return "";
  if (str.length <= maxChars) return str;
  return str.slice(0, Math.max(1, maxChars - 1)) + "…";
};

export const renderPieChartCustomizedLabel = (
  props: PieLabelRenderProps & { name: string },
) => {
  const { cx, cy, innerRadius, outerRadius, midAngle, percent, value } = props;

  if (cx == null || cy == null || innerRadius == null || outerRadius == null) {
    return null;
  }

  if ((percent ?? 1) < 0.06 || (value as number) < 1) {
    return null;
  }

  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = Number(cx) + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
  const y = Number(cy) + radius * Math.sin(-(midAngle ?? 0) * RADIAN);

  const maxChars = Math.max(
    8,
    Math.floor((outerRadius - innerRadius) / 6) + 10,
  );
  const label = ellipsis(props.name || "", maxChars);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="middle"
      unicodeBidi="plaintext"
      fontSize="12px"
    >
      <tspan x={x} dy="-0.35em">
        {label}
      </tspan>
      <tspan x={x} dy="1.2em">
        {value}
      </tspan>
    </text>
  );
};

/**
 * Labels the numbers with the window they actually cover. A custom date span
 * used to fall back to "Today", which mislabelled every figure on the page.
 */
export const getRangeLabel = (
  report: Pick<GetReportsResponseDto, "range" | "scope"> | undefined,
  t: TFunction,
) => {
  if (report?.scope?.length === 2) {
    return `${standardDate<string>(report.scope[0])} - ${standardDate<string>(report.scope[1])}`;
  }

  return t(
    `entries.table.entry.filters.time.${(report?.range || Range.TODAY).toLowerCase()}`,
  );
};

/** Must match `OTHER_ORIGIN_SENTINEL` in the API's report.service.ts. */
const OTHER_ORIGIN_SENTINEL = "__other__";

/** The lead-source breakdown caps at nine named origins and rolls the rest
 * into one bucket server side, so the shares it displays never silently
 * disagree with the true total. */
export const getOriginLabel = (origin: string | null, t: TFunction): string => {
  if (origin === OTHER_ORIGIN_SENTINEL) {
    return t("reports.origin.other");
  }

  return origin || t("reports.origin.unknown");
};

/**
 * Rounding straight to a whole percent turns a real but small share or change
 * into "0%", which reads as nothing happened at all. Keeping up to two
 * decimal places - trimmed back down when they're not needed - keeps a
 * genuinely tiny value visible without cluttering an ordinary one.
 */
export const formatReportPercent = (fraction: number): string => {
  const percent = Math.round(fraction * 100 * 100) / 100;

  return Number.isInteger(percent)
    ? `${percent}`
    : percent.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
};

export const CHARTS_HEIGHT = "24rem";

export const SERIES_TO_KIND: Record<string, ActivityEntityKind> = {
  dealsClosed: ActivityEntityKind.KANBAN_CARD,
  tasksDone: ActivityEntityKind.TASK,
  entriesConverted: ActivityEntityKind.ENTRY,
};

export const onViewEntitiesFromCards = (
  filter: {
    range?: Range;
    scope?: [string, string];
    kind?: EntryKind;
    done?: boolean;
    reminder?: TaskFilterReminder;
    dateField?: KanbanCardDateField;
    stage?: KanbanColumnStageType;
  },
  kind: ReportKinds,
  navigate: NavigateFunction,
) => {
  switch (kind) {
    case ReportKinds.ENTRY:
      navigate("/entries", { state: { useFilter: filter } });
      break;
    case ReportKinds.TASK:
      navigate("/tasks", { state: { useFilter: filter } });
      break;
    case ReportKinds.DEAL:
      navigate("/deals", {
        state: {
          useFilter: {
            dateRange: filter.range,
            scope: filter.scope,
            dateField: filter.dateField ?? KanbanCardDateField.CREATED_AT,
            stage: filter.stage,
          },
        },
      });
      break;
    default:
      break;
  }
};
